import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { formatUSD, MIN_ORDER_GRAMS } from "../lib/api";

const BAR_TIERS = [
  { label: "12.5 kg Good Delivery", grams: 12500 },
  { label: "10 kg Cast Bar", grams: 10000 },
  { label: "5 kg Cast Bar", grams: 5000 },
  { label: "2.5 kg Cast Bar", grams: 2500 },
  { label: "1 kg Minted Bar", grams: 1000 },
  { label: "500 g Minted Bar", grams: 500 },
];

const PRICE_PER_GRAM = 120;

const WholesaleCalculator = ({ onTransfer }) => {
  const [grams, setGrams] = useState(2500);

  const breakdown = useMemo(() => {
    let remaining = grams;
    return BAR_TIERS.map((tier) => {
      const count = Math.floor(remaining / tier.grams);
      remaining -= count * tier.grams;
      return { ...tier, count };
    }).filter((b) => b.count > 0);
  }, [grams]);

  const total = grams * PRICE_PER_GRAM;

  return (
    <section id="calculator" className="py-24 lg:py-32 bg-[#141312] relative overflow-hidden">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl mb-14"
        >
          <p className="text-xs font-medium tracking-[0.3em] uppercase text-[#D4AF37] mb-4">Wholesale Calculator</p>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#FAF8F5]">
            Price your order <em className="text-[#D4AF37]">in seconds.</em>
          </h2>
          <p className="mt-5 text-sm sm:text-base leading-relaxed text-[#FAF8F5]/60 font-light">
            Slide to your target weight — from the 500 g minimum up to 50 kg per request — and see the
            wholesale value and bar mix instantly.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="lg:col-span-7 bg-[#1F1D1B] border border-[#C5A059]/25 p-8 lg:p-10"
          >
            <div className="flex items-baseline justify-between gap-6 flex-wrap">
              <label className="text-[11px] tracking-[0.24em] uppercase text-[#FAF8F5]/60">Order weight</label>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="calculator-weight-input"
                  type="number"
                  min={MIN_ORDER_GRAMS}
                  step={500}
                  value={grams}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (!Number.isNaN(v)) setGrams(Math.min(50000, Math.max(MIN_ORDER_GRAMS, v)));
                  }}
                  className="w-32 rounded-none bg-transparent border-[#C5A059]/40 text-[#FAF8F5] font-mono text-right focus-visible:ring-[#D4AF37]/50"
                />
                <span className="font-mono text-sm text-[#FAF8F5]/60">g</span>
              </div>
            </div>
            <div className="mt-8">
              <Slider
                data-testid="calculator-slider-input"
                value={[grams]}
                min={MIN_ORDER_GRAMS}
                max={50000}
                step={500}
                onValueChange={(v) => setGrams(v[0])}
                className="w-full"
              />
              <div className="flex justify-between mt-3 font-mono text-[10px] tracking-[0.16em] text-[#FAF8F5]/40 uppercase">
                <span>500 g min</span>
                <span>50 kg</span>
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-[#C5A059]/20 flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="text-[11px] tracking-[0.24em] uppercase text-[#FAF8F5]/60">Estimated total</p>
                <p data-testid="calculator-total-value" className="font-mono text-4xl lg:text-5xl text-[#D4AF37] mt-2">
                  {formatUSD(total)}
                </p>
                <p className="font-mono text-xs text-[#FAF8F5]/50 mt-2">
                  {grams.toLocaleString()} g × $120.00 / g
                </p>
              </div>
              <Button
                data-testid="calculator-transfer-btn"
                onClick={() => onTransfer(grams)}
                className="rounded-none h-12 px-8 bg-[#D4AF37] text-[#141312] hover:bg-[#FAF8F5] text-[12px] tracking-[0.14em] uppercase transition-colors duration-500"
              >
                Use in Quote Request
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="lg:col-span-5"
            data-testid="calculator-breakdown"
          >
            <p className="text-[11px] tracking-[0.24em] uppercase text-[#FAF8F5]/60 mb-5">Suggested bar mix</p>
            <div className="divide-y divide-[#C5A059]/15 border-y border-[#C5A059]/15">
              {breakdown.map((b) => (
                <div key={b.label} className="flex items-center justify-between py-4">
                  <span className="font-mono text-sm text-[#FAF8F5]/85">
                    <span className="text-[#D4AF37]">{b.count} ×</span> {b.label}
                  </span>
                  <span className="font-mono text-sm text-[#FAF8F5]/50">{formatUSD(b.count * b.grams * PRICE_PER_GRAM)}</span>
                </div>
              ))}
              {breakdown.length === 0 && (
                <p className="py-6 text-sm text-[#FAF8F5]/50">Move the slider above to compose your wholesale order.</p>
              )}
            </div>
            <p className="mt-5 text-xs leading-relaxed text-[#FAF8F5]/45 font-light">
              Indicative wholesale pricing at $120.00 per gram. Final quotation is confirmed by our sales desk
              with assay and settlement terms.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WholesaleCalculator;
